import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormApi } from 'final-form';
import { Field } from 'react-final-form';
import MultiSelect, { IOption } from '../../components/mulitiSelect/MultiSelect';
import styles from './ReportList.module.scss';
import { ISSFiltersFormValues, validateSSReportFilters } from './SSReport';
import { useDispatch, useSelector } from 'react-redux';
import { toNumericId } from '../../utils/commonUtils';
import APPCONSTANTS, { USER_REPORT_ROLES } from '../../constants/appConstants';
import { fetchUsersByRoleNamesRequest } from '../../store/user/actions';
import { usersByRolesLoadingSelector, usersByRolesSelector } from '../../store/user/selectors';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import CustomTooltip from '../../components/tooltip';
import ResetIcon from '../../assets/images/reset.svg';
import { clearBranchList, fetchBranchListRequest } from '../../store/branch/actions';
import { branchListSelector, branchLoadingSelector } from '../../store/branch/selectors';
import toastCenter from '../../utils/toastCenter';
import DateRangeFields from './DateRangeFields';
import RegionFilterFields from './RegionFilterFields';
import { ssReportLoadingSelector } from '../../store/report/selectors';

interface ISSFilterFieldsProps {
  form: FormApi<any>;
  name: string;
  values: any;
  rowsPerPage?: number;
  onFetchSSReport?: (formValues: ISSFiltersFormValues, page: number, rowsPerPage: number) => void;
}

const SSFilterFields = ({
  form,
  name = 'ssreport',
  values,
  rowsPerPage = APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE,
  onFetchSSReport
}: ISSFilterFieldsProps): React.ReactElement => {
  const usersByRoles = useSelector(usersByRolesSelector);
  const usersByRolesLoading = useSelector(usersByRolesLoadingSelector);
  const branchList = useSelector(branchListSelector);
  const branchLoading = useSelector(branchLoadingSelector);
  const ssReportLoading = useSelector(ssReportLoadingSelector);
  const dispatch = useDispatch();
  const branchMultiSelectRef = useRef<any>(null);
  const userRoleMultiSelectRef = useRef<any>(null);
  const userNameMultiSelectRef = useRef<any>(null);

  const fieldName = useCallback((field: keyof ISSFiltersFormValues) => `${name}.${field}`, [name]);
  const formValues: ISSFiltersFormValues = values?.[name] || {};
  const chiefdomId = toNumericId(formValues.upazila);
  const roleNames = useMemo(() => {
    return (formValues.userRoles || [])
      .map((role) => role?.name)
      .filter((role): role is string => Boolean(role));
  }, [formValues.userRoles]);
  const branchIds = useMemo(
    () =>
      (formValues.branches || [])
        .map((branch) => toNumericId(branch?.id))
        .filter((id): id is number => Number.isFinite(id)),
    [formValues.branches]
  );
  const debouncedRoleNames = useDebouncedValue(roleNames, 500);
  const debouncedBranchIds = useDebouncedValue(branchIds, 500);

  const handleResetFilters = () => {
    form.change(fieldName('fromDate'), undefined);
    form.change(fieldName('toDate'), undefined);
    form.change(fieldName('division'), null);
    form.change(fieldName('district'), null);
    form.change(fieldName('upazila'), null);
    form.change(fieldName('branches'), []);
    form.change(fieldName('userRoles'), []);
    form.change(fieldName('userNames'), []);
  };

  const handleViewReport = () => {
    if (!validateSSReportFilters(formValues)) {
      toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.SS_REPORT_FILTER_VALIDATION_ERROR);
      return;
    }

    onFetchSSReport?.(formValues, APPCONSTANTS.NUMBER.ONE, rowsPerPage);
  };

  useEffect(() => {
    if (!debouncedRoleNames.length || !debouncedBranchIds.length) {
      form.change(fieldName('userNames'), []);
      return;
    }
    dispatch(
      fetchUsersByRoleNamesRequest({
        userRoles: debouncedRoleNames,
        branchIds: debouncedBranchIds
      })
    );
  }, [debouncedBranchIds, debouncedRoleNames, dispatch]);

  useEffect(() => {
    const userIdSet = new Set(usersByRoles.map(user => user?.id).filter(Boolean));
    const currentUsers = formValues.userNames || [];
    const validUsers = currentUsers.filter(
      user => userIdSet.has(user?.id)
    );

    if (validUsers.length !== currentUsers.length) {
      form.change(fieldName('userNames'), validUsers);
    }
  }, [usersByRoles, formValues.userNames, form, fieldName]);

  useEffect(() => {
    const branchIdSet = new Set(branchList.map((branch) => branch?.id).filter(Boolean));
    const currentBranches = formValues.branches || [];
    const validBranches = currentBranches.filter((branch) => branchIdSet.has(branch?.id));

    if (validBranches.length !== currentBranches.length) {
      form.change(fieldName('branches'), validBranches);
    }
  }, [branchList, formValues.branches, form, fieldName]);

  useEffect(() => {
    // When chiefdom (upazila) changes, immediately clear branch and user selections
    // to avoid sending stale branchIds/userNames while the new branch list is loading.
    form.change(fieldName('branches'), []);
    form.change(fieldName('userNames'), []);

    if (!chiefdomId) {
      dispatch(clearBranchList());
      return;
    }

    dispatch(
      fetchBranchListRequest({
        chiefdomId: Number(chiefdomId)
      })
    );
  }, [chiefdomId, dispatch, fieldName]);

  return (
    <div className='col-auto'>
      <div className={`${styles.sideMenu} ${styles.customSidemenu}`}>
        <div className='d-flex justify-content-between align-items-center'>
          <div className={`card-header bg-transparent ${styles.header}`}>
            <span className={styles.headerLabel}>Filters</span>
          </div>
          <CustomTooltip title='Reset'>
            <button
              type='button'
              onClick={handleResetFilters}
              className='btn p-0 border-0 bg-transparent pointer'
              aria-label='Reset filters'
            >
              <img src={ResetIcon} alt='reset-icon' />
            </button>
          </CustomTooltip>
        </div>
        <hr />
        <DateRangeFields
          form={form}
          formValues={formValues}
          fromDateFieldName={fieldName('fromDate')}
          toDateFieldName={fieldName('toDate')}
        />
        <RegionFilterFields form={form} name={name} values={values} />
        <Field
          name={fieldName('branches')}
          render={({ input }) => (
            <div className='py-dot625'>
              <p className='mb-dot25'>Branch</p>
              <MultiSelect
                label='Branch'
                labelPlural='Branches'
                selectedValue={input.value || []}
                loading={branchLoading}
                options={branchList}
                multiSelectRef={branchMultiSelectRef}
                handleFilterChange={(value: IOption[]) => input.onChange(value)}
                labelKey='name'
                valueKey='id'
                isCustomValueEnabled={false}
                isApplyButtonNeeded={false}
              />
            </div>
          )}
        />
        <Field
          name={fieldName('userRoles')}
          render={({ input }) => (
            <div className='py-dot625'>
              <p className='mb-dot25'>User Role</p>
              <MultiSelect
                label='User Role'
                labelPlural='User Roles'
                selectedValue={input.value || []}
                options={USER_REPORT_ROLES}
                multiSelectRef={userRoleMultiSelectRef}
                handleFilterChange={(value: any[]) => input.onChange(value)}
                labelKey='name'
                valueKey='id'
                isCustomValueEnabled={false}
                isApplyButtonNeeded={false}
              />
            </div>
          )}
        />
        <Field
          name={fieldName('userNames')}
          render={({ input }) => (
            <div className='py-dot625'>
              <p className='mb-dot25'>User Name</p>
              <MultiSelect
                label='User Name'
                labelPlural='User Names'
                selectedValue={input.value || []}
                loading={usersByRolesLoading}
                options={usersByRoles}
                multiSelectRef={userNameMultiSelectRef}
                handleFilterChange={(value: IOption[]) => input.onChange(value)}
                labelKey='displayName'
                valueKey='id'
                isCustomValueEnabled={false}
                isApplyButtonNeeded={false}
              />
            </div>
          )}
        />
        <button
          onClick={handleViewReport}
          type='button'
          className='mt-2 btn primary-btn w-100'
          disabled={ssReportLoading}
        >
          {ssReportLoading ? 'Loading...' : 'View Report'}
        </button>
      </div>
    </div>
  );
};

export default SSFilterFields;
